/**
 * Utilidades de formato reutilizables para la pantalla de comprobantes.
 * Todas las funciones son puras y tolerantes a valores nulos/indefinidos.
 */

const currencyFormatter = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Formatea un número como moneda argentina (ARS). */
export const formatCurrency = (value) => {
  const number = Number(value)
  if (value === null || value === undefined || Number.isNaN(number)) return "-"
  return currencyFormatter.format(number)
}

/** Formatea una fecha ISO / Date a DD/MM/YYYY. */
export const formatDate = (value) => {
  if (!value) return "-"
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  const day = String(date.getDate()).padStart(2, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

/** Formatea el número de comprobante como 00008-00000125. */
export const formatComprobanteNumber = (ptoVenta, nroComprobante) => {
  if (ptoVenta === null || ptoVenta === undefined) return "-"
  if (nroComprobante === null || nroComprobante === undefined) return "-"
  const pv = String(ptoVenta).replace(/\D/g, "").padStart(4, "0")
  const nro = String(nroComprobante).replace(/\D/g, "").padStart(8, "0")
  return `${pv}-${nro}`
}

/** Formatea un CUIT/DNI: 20-12345678-9 cuando corresponde. */
export const formatCuit = (value) => {
  if (!value) return "-"
  const digits = String(value).replace(/\D/g, "")
  if (digits.length === 11) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`
  }
  return String(value)
}

/**
 * Intenta parsear un valor que puede ser un objeto o un string JSON.
 * Devuelve el objeto parseado o null si no es válido.
 */
export const safeParseJson = (value) => {
  if (value === null || value === undefined) return null
  if (typeof value === "object") return value
  if (typeof value === "string") {
    try {
      return JSON.parse(value)
    } catch {
      return null
    }
  }
  return null
}

/** Devuelve un string JSON prettificado o el valor original si no se puede parsear. */
export const prettyJson = (value) => {
  const parsed = safeParseJson(value)
  if (parsed !== null) return JSON.stringify(parsed, null, 2)
  if (value === null || value === undefined) return ""
  return String(value)
}

export const getEstado = (comprobante) => {
  const candidates = [comprobante?.json_req_afip, comprobante?.rta_afip, comprobante?.estado]

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.length <= 40 && !candidate.trim().startsWith("{")) {
      return candidate
    }
    const parsed = safeParseJson(candidate)
    if (parsed && typeof parsed === "object") {
      const estado = parsed.estado ?? parsed.Estado ?? parsed.status ?? parsed.resultado ?? parsed.Resultado
      if (estado) return String(estado)
    }
  }
  return null
}


/**
 * Mapea el código numérico de estado de un recibo a una etiqueta + color de Chip.
 * 357 = aprobado, 358 = pendiente, 359 = rechazado. Cualquier otro valor
 * (incluye null / "null" / undefined) cae en "Sin estado".
 */
export const getEstadoReciboConfig = (codigo) => {
  switch (Number(codigo)) {
    case 357:
      return { label: "Aprobado", color: "success" }
    case 358:
      return { label: "Pendiente", color: "warning" }
    case 359:
      return { label: "Rechazado", color: "error" }
    default:
      return { label: "Sin estado", color: "default" }
  }
}

/**
 * Separa la línea "Recibo N°..." de su sufijo " - Estado: <código>".
 * El endpoint de pagos arma el estado dentro del texto, así que lo extraemos
 * para mostrarlo como Chip y dejar el encabezado limpio.
 * Devuelve { encabezado, estado } (estado puede ser null si no venía).
 */
export const parseEncabezadoRecibo = (linea) => {
  if (typeof linea !== "string") return { encabezado: linea ?? "", estado: null }
  const match = linea.match(/\s*[-|]\s*Estado:\s*([^\s|]+)\s*$/i)
  if (!match) return { encabezado: linea, estado: null }
  return { encabezado: linea.slice(0, match.index).trim(), estado: match[1] }
}

const montoTextoFormatter = new Intl.NumberFormat("es-AR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Reformatea los montos que vienen embebidos en las líneas de texto del
 * endpoint de pagos ("$10000" / "$10000.5") al formato es-AR: punto para los
 * miles y coma para los decimales ("$10.000,00" / "$10.000,50").
 * Solo toca los números precedidos por "$", el resto del texto queda igual.
 */
export const formatearMontosEnTexto = (linea) => {
  if (typeof linea !== "string") return linea
  return linea.replace(/\$\s?(\d+(?:\.\d+)?)/g, (match, numero) => {
    const parsed = Number(numero)
    if (Number.isNaN(parsed)) return match
    return `$${montoTextoFormatter.format(parsed)}`
  })
}

export const getEstadoConfig = (estadoRaw) => {
  const estado = (estadoRaw ?? "").toString().trim().toUpperCase()

  const aprobado = ["Aprobado", "APROBADO", "APROBADA", "AUTORIZADO", "AUTORIZADA", "OK", "ACEPTADO"]
  const anulado = ["Anulado", "ANULADO", "RECHAZADA", "ERROR", "DENEGADO"]

  if (aprobado.includes(estado)) {
    return { label: "Aprobado", color: "success" }
  }
  if (anulado.includes(estado)) {
    return { label: "anulado", color: "error" }
  }

  if (!estado) {
    return { label: "Sin estado", color: "default" }
  }
  return { label: estadoRaw, color: "default" }
}
